import React, { useState, useEffect, useRef } from 'react'
import { api, formatDate, formatCurrency } from '../../utils/api'

const CARRIERS = {
  royal_mail: { name: 'Royal Mail', color: '#E74C3C', url: 'https://send.royalmail.com/login?returnUrl=%2F' },
  fedex: { name: 'FedEx', color: '#4D148C', url: 'https://www.fedex.com/shippingplus/en-gb/' }
}

function ReturnItemsTab() {
  const [data, setData] = useState({ royal_mail: [], fedex: [], label_sent: [], total: 0, label_sent_count: 0 })
  const [loading, setLoading] = useState(true)
  const [selectedReturn, setSelectedReturn] = useState(null)
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState(null)
  const [showKpiModal, setShowKpiModal] = useState(null) // 'labelSent'
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    try {
      const response = await api.get('/api/orders/returns/booked')
      setData({
        royal_mail: response.royal_mail || [],
        fedex: response.fedex || [],
        label_sent: response.label_sent || [],
        total: response.total || 0,
        label_sent_count: response.label_sent_count || 0
      })
    } catch (error) {
      console.error('Failed to load booked returns:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleSendLabel({ file, trackingNumber, shippingCost }) {
    if (!selectedReturn || !file) return
    setSending(true)
    setSendResult(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (trackingNumber) formData.append('trackingNumber', trackingNumber)
      if (shippingCost) formData.append('shippingCost', shippingCost)

      const apiBase = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiBase}/api/orders/returns/${selectedReturn.id}/send-label`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })
      const result = await response.json()
      if (!response.ok || result.error) throw new Error(result.error || 'Failed to send label')

      setSendResult({ type: 'success', message: `Label sent to ${selectedReturn.customer_email}` })
      setSelectedReturn(null)
      loadData()
    } catch (error) {
      setSendResult({ type: 'error', message: error.message })
    } finally {
      setSending(false)
      setTimeout(() => setSendResult(null), 5000)
    }
  }

  async function handleCancelReturn(returnItem) {
    if (!confirm(`Are you sure?\n\nCase #${returnItem.id} — ${returnItem.product_title}\n\nThis will remove the return booking. The case will remain open.`)) return
    setCancelling(true)
    try {
      await api.patch(`/api/orders/returns/${returnItem.id}`, {
        status: 'return_approved'
      })
      setSendResult({ type: 'success', message: `Return booking cancelled for case #${returnItem.id}` })
      setSelectedReturn(null)
      loadData()
    } catch (error) {
      setSendResult({ type: 'error', message: error.message })
    } finally {
      setCancelling(false)
      setTimeout(() => setSendResult(null), 5000)
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div className="loading-spinner" />
        <p style={{ color: 'var(--muted)', marginTop: 12 }}>Loading return items...</p>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 20 }}>
      {sendResult && (
        <div style={{
          padding: '10px 16px',
          marginBottom: 12,
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 500,
          background: sendResult.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color: sendResult.type === 'success' ? '#16a34a' : '#dc2626',
          border: `1px solid ${sendResult.type === 'success' ? '#bbf7d0' : '#fecaca'}`
        }}>
          {sendResult.message}
        </div>
      )}

      {/* Stats Tiles */}
      <div className="statsRow">
        <div className="statBox">
          <div className="statValue">{data.total}</div>
          <div className="statLabel">Awaiting Label</div>
        </div>
        <div
          className="statBox"
          style={{
            cursor: data.label_sent_count > 0 ? 'pointer' : 'default',
            background: data.label_sent_count > 0 ? '#EBF5FF' : undefined
          }}
          onClick={() => data.label_sent_count > 0 && setShowKpiModal('labelSent')}
        >
          <div className="statValue">{data.label_sent_count}</div>
          <div className="statLabel">Label Sent</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-sm btn-secondary" onClick={loadData} disabled={loading}>
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16 }}>
        <CarrierColumn
          carrierId="royal_mail"
          returns={data.royal_mail}
          selectedId={selectedReturn?.id}
          onSelect={r => setSelectedReturn(r)}
        />
        <CarrierColumn
          carrierId="fedex"
          returns={data.fedex}
          selectedId={selectedReturn?.id}
          onSelect={r => setSelectedReturn(r)}
        />
      </div>

      {selectedReturn && (
        <DetailPanel
          returnItem={selectedReturn}
          sending={sending}
          cancelling={cancelling}
          onSendLabel={handleSendLabel}
          onCancelReturn={handleCancelReturn}
          onClose={() => setSelectedReturn(null)}
        />
      )}

      {/* Label Sent KPI Modal */}
      {showKpiModal === 'labelSent' && (
        <div className="modal-overlay" onClick={() => setShowKpiModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h2>Label Sent — Awaiting Refund</h2>
              <button className="modal-close" onClick={() => setShowKpiModal(null)}>&times;</button>
            </div>
            <div className="modal-body-scroll" style={{ maxHeight: 400 }}>
              {data.label_sent.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
                  No returns found
                </div>
              ) : (
                <table className="table" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Case #</th>
                      <th>Customer</th>
                      <th>Product</th>
                      <th>Carrier</th>
                      <th>Tracking</th>
                      <th>Label Sent</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.label_sent.map(r => {
                      const carrier = CARRIERS[r.return_carrier]
                      return (
                        <tr
                          key={r.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setShowKpiModal(null)
                            setSelectedReturn(r)
                          }}
                        >
                          <td style={{ fontFamily: 'monospace' }}>{r.id}</td>
                          <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.customer_name || r.customer_email || '-'}
                          </td>
                          <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.product_title || '-'}
                          </td>
                          <td>
                            <span style={{
                              background: carrier?.color || '#999',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 11
                            }}>
                              {carrier?.name || 'Unknown'}
                            </span>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                            {r.return_tracking_number || '-'}
                          </td>
                          <td>{r.return_label_sent_at ? formatDate(r.return_label_sent_at) : '-'}</td>
                          <td>{r.return_shipping_cost ? formatCurrency(r.return_shipping_cost) : '-'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowKpiModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function DetailPanel({ returnItem, sending, cancelling, onSendLabel, onCancelReturn, onClose }) {
  const [copied, setCopied] = useState(null)
  const [labelFile, setLabelFile] = useState(null)
  const [trackingNumber, setTrackingNumber] = useState('')
  const [shippingCost, setShippingCost] = useState('')
  const fileInputRef = useRef(null)
  const carrier = CARRIERS[returnItem.return_carrier] || CARRIERS.royal_mail

  let boxCount = 1
  let parsedDims = null
  try {
    parsedDims = JSON.parse(returnItem.return_dimensions || '{}')
    boxCount = Array.isArray(parsedDims) ? parsedDims.length : 1
  } catch { /* ignore */ }

  async function copyToClipboard(text, field) {
    await navigator.clipboard.writeText(text)
    setCopied(field)
    setTimeout(() => setCopied(null), 1500)
  }

  function handleFileSelect(e) {
    const file = e.target.files[0]
    if (file) setLabelFile(file)
  }

  function handleSubmit() {
    if (!labelFile) return
    onSendLabel({ file: labelFile, trackingNumber, shippingCost })
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: 420,
      background: 'white',
      boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{ padding: 'var(--space-4)', background: carrier.color, color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{
                background: 'rgba(255,255,255,0.3)',
                padding: '4px 10px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600
              }}>
                {carrier.name}
              </span>
              <span style={{
                background: returnItem.status === 'return_label_sent' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)',
                padding: '4px 10px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600
              }}>
                {returnItem.status === 'return_label_sent' ? 'Label Sent' : 'Awaiting Label'}
              </span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{returnItem.product_title || 'Unknown Product'}</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>Case #{returnItem.id}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}>
            &times;
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)' }}>
        {/* Quick Copy Section */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>QUICK COPY</div>
          <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', padding: '4px 12px' }}>
            <QuickCopyRow label="Case #" value={String(returnItem.id)} onCopy={copyToClipboard} copied={copied} mono />
            <QuickCopyRow label="Customer" value={returnItem.customer_name} onCopy={copyToClipboard} copied={copied} />
            <QuickCopyRow label="Email" value={returnItem.customer_email} onCopy={copyToClipboard} copied={copied} />
            <QuickCopyRow label="Order" value={returnItem.order_name} onCopy={copyToClipboard} copied={copied} mono />
            {(() => {
              try {
                const addr = JSON.parse(returnItem.return_address || '{}')
                return addr.zip ? <QuickCopyRow label="Postcode" value={addr.zip} onCopy={copyToClipboard} copied={copied} mono /> : null
              } catch { return null }
            })()}
          </div>
        </div>

        {/* Customer Address */}
        {returnItem.return_address && (() => {
          try {
            const addr = JSON.parse(returnItem.return_address)
            const lines = [addr.address1, addr.address2, addr.city, addr.province, addr.zip, addr.country].filter(Boolean)
            const fullAddress = lines.join(', ')
            return (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>CUSTOMER ADDRESS</div>
                <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', padding: '4px 12px' }}>
                  <QuickCopyRow label="Full Address" value={fullAddress} onCopy={copyToClipboard} copied={copied} />
                </div>
              </div>
            )
          } catch { return null }
        })()}

        {/* Carrier Reason */}
        {returnItem.return_carrier_reason && (
          <div style={{ background: 'var(--color-bg-secondary)', padding: 'var(--space-3)', borderRadius: 'var(--radius)', marginBottom: 'var(--space-4)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Carrier Assignment</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{returnItem.return_carrier_reason}</div>
          </div>
        )}

        {/* Package Details */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>PACKAGE</div>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            {boxCount} box{boxCount !== 1 ? 'es' : ''} &middot; {returnItem.return_weight}kg total
          </div>
          {parsedDims && (
            Array.isArray(parsedDims) ? (
              parsedDims.map((box, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Box {i + 1}: {box.length}&times;{box.width}&times;{box.height}cm
                </div>
              ))
            ) : parsedDims.length ? (
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                {parsedDims.length}&times;{parsedDims.width}&times;{parsedDims.height}cm
              </div>
            ) : null
          )}
        </div>

        {/* Return Info */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>RETURN INFO</div>
          <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', padding: '4px 12px' }}>
            <QuickCopyRow label="Reason" value={returnItem.reason_name} onCopy={copyToClipboard} copied={copied} />
            <QuickCopyRow label="Booked" value={returnItem.return_booked_at ? formatDate(returnItem.return_booked_at) : null} onCopy={copyToClipboard} copied={copied} />
            {returnItem.return_tracking_number && (
              <QuickCopyRow label="Tracking" value={returnItem.return_tracking_number} onCopy={copyToClipboard} copied={copied} mono />
            )}
            {returnItem.return_shipping_cost && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
                <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>Shipping Cost</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{formatCurrency(returnItem.return_shipping_cost)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Label Sent State */}
        {returnItem.status === 'return_label_sent' ? (
          <div style={{
            padding: 16,
            background: 'rgba(59, 178, 115, 0.08)',
            border: '1px solid var(--green-onions, #059669)',
            borderRadius: 'var(--radius)',
            textAlign: 'center'
          }}>
            <span style={{ color: 'var(--green-onions, #059669)', fontSize: 16, marginRight: 8 }}>&#10003;</span>
            <span style={{ fontWeight: 600, color: 'var(--green-onions, #059669)' }}>Label Sent</span>
            {returnItem.return_label_url && (
              <div style={{ marginTop: 8 }}>
                <a href={returnItem.return_label_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--color-primary)' }}>View Label</a>
              </div>
            )}
          </div>
        ) : (
          /* Send Label Form */
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: 'var(--color-text-muted)' }}>
              SEND LABEL {boxCount > 1 ? `(${boxCount} BOXES)` : ''}
            </div>

            {/* File Upload */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Label File <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              {labelFile ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  background: 'rgba(59, 178, 115, 0.08)',
                  border: '1px solid var(--green-onions, #059669)',
                  borderRadius: 'var(--radius)'
                }}>
                  <span style={{ color: 'var(--green-onions, #059669)', fontSize: 14 }}>&#10003;</span>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {labelFile.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setLabelFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
                    style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 16 }}
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: '2px dashed var(--color-border)',
                    borderRadius: 'var(--radius)',
                    padding: '20px 16px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: 'var(--color-bg-secondary)',
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{ fontSize: 24, marginBottom: 4 }}>&#128206;</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Click to upload label</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>PDF, PNG or JPG</div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            {/* Tracking Number */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Tracking Number (optional)</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. AB123456789GB"
                value={trackingNumber}
                onChange={e => setTrackingNumber(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            {/* Shipping Cost */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Return Shipping Cost (&pound;)</label>
              <input
                type="number"
                className="form-input"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={shippingCost}
                onChange={e => setShippingCost(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={sending || !labelFile}
              style={{ width: '100%' }}
            >
              {sending ? 'Uploading & Sending...' : 'Upload & Send Label to Customer'}
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => onCancelReturn(returnItem)}
              disabled={sending || cancelling}
              style={{ width: '100%', marginTop: 12, color: '#dc2626', borderColor: '#fca5a5' }}
            >
              {cancelling ? 'Cancelling...' : 'Return No Longer Required'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function QuickCopyRow({ label, value, onCopy, copied, mono }) {
  if (!value) return null
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid var(--color-border)'
    }}>
      <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 13,
          fontWeight: mono ? 500 : 600,
          fontFamily: mono ? 'monospace' : 'inherit',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {value}
        </span>
        <button
          className="btn btn-xs"
          onClick={() => onCopy(value, label)}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            background: '#333',
            color: 'white',
            border: 'none'
          }}
        >
          {copied === label ? '\u2713' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

function CarrierColumn({ carrierId, returns, selectedId, onSelect }) {
  const carrier = CARRIERS[carrierId]
  return (
    <div style={{
      flex: '0 0 280px',
      background: 'var(--bg-secondary)',
      borderRadius: 'var(--radius)',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 280px)',
      overflow: 'hidden'
    }}>
      <div style={{
        padding: 12,
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: carrier.color }} />
          <span
            onClick={() => window.open(carrier.url, '_blank', 'width=1200,height=800')}
            style={{ fontWeight: 600, cursor: 'pointer' }}
            onMouseEnter={e => e.target.style.textDecoration = 'underline'}
            onMouseLeave={e => e.target.style.textDecoration = 'none'}
            title={`Open ${carrier.name} shipping portal`}
          >
            {carrier.name}
          </span>
        </div>
        <span style={{
          background: '#333',
          color: 'white',
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 600
        }}>{returns.length}</span>
      </div>
      <div style={{ padding: 8, flex: 1, overflowY: 'auto' }}>
        {returns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No returns</div>
        ) : (
          returns.map(r => (
            <ReturnCard
              key={r.id}
              returnItem={r}
              isSelected={selectedId === r.id}
              onClick={() => onSelect(r)}
            />
          ))
        )}
      </div>
    </div>
  )
}

function ReturnCard({ returnItem, isSelected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: isSelected ? 'rgba(59, 178, 115, 0.1)' : 'white',
        border: isSelected ? '2px solid var(--green-onions, #059669)' : '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: 12,
        marginBottom: 8,
        cursor: 'pointer',
        transition: 'all 0.15s'
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>
        Case #{returnItem.id} {returnItem.order_name ? `\u00b7 ${returnItem.order_name}` : ''}
      </div>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
        {returnItem.product_title || 'Unknown Product'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
        {returnItem.customer_name || returnItem.customer_email || 'Unknown'}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {returnItem.return_weight && (
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{returnItem.return_weight}kg</span>
        )}
        <span style={{
          fontSize: 10,
          padding: '2px 8px',
          borderRadius: 4,
          fontWeight: 600,
          background: '#fef3c7',
          color: '#92400e'
        }}>
          Awaiting Label
        </span>
      </div>
      {returnItem.return_booked_at && (
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
          Booked: {formatDate(returnItem.return_booked_at)}
        </div>
      )}
    </div>
  )
}

export default ReturnItemsTab
